import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Badge, Column, ConfirmationModal, Row, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { CopyToClipboard } from '~/components/CopyToClipboard/CopyToClipboard';
import { useLocaleKeys } from '~/i18n';
export const GetShareByLinkDialogTID = (parentTestId)=>getTestIds(parentTestId);
export const ShareByLinkDialog = (props)=>{
    const { ShareByLinkDialogTID } = useMemo(()=>GetShareByLinkDialogTID(), []);
    const { t: shareByLinkTexts } = useLocaleKeys('vendorOnboarding.adHocBillModal.confirmationDialogs.shareByLink');
    const [dialogStep, setDialogStep] = useState({
        step: 'warning'
    });
    const [linkUrl, setLinkUrl] = useState('');
    const handleGenerateLink = async ()=>{
        if (dialogStep.step === 'generating') return;
        setDialogStep({
            step: 'generating'
        });
        try {
            const result = await props.onGenerateLink();
            if (result.success && result.linkUrl != null) {
                setLinkUrl(result.linkUrl);
                setDialogStep({
                    step: 'linkReady'
                });
            } else {
                setDialogStep({
                    step: 'error',
                    submitSucceeded: result.success
                });
            }
        } catch (unused) {
            setDialogStep({
                step: 'error',
                submitSucceeded: false
            });
        }
    };
    if (dialogStep.step === 'warning' || dialogStep.step === 'generating') {
        return /*#__PURE__*/ _jsx(ConfirmationModal, {
            bi: {
                primaryButton: {
                    name: 'share_by_link_generate'
                },
                secondaryButton: {
                    name: 'share_by_link_cancel'
                }
            },
            testId: ShareByLinkDialogTID.Root(),
            title: shareByLinkTexts.title(),
            textAlignCenter: false,
            width: "520px",
            secondaryButtonLabel: shareByLinkTexts.cancelButton(),
            secondaryButtonTestId: ShareByLinkDialogTID.CancelButton(),
            onSecondaryButtonClick: props.onCancel,
            onCloseButtonClick: props.onCancel,
            primaryButtonLabel: shareByLinkTexts.generateLinkButton(),
            primaryButtonTestId: ShareByLinkDialogTID.GenerateLinkButton(),
            primaryButtonLoading: dialogStep.step === 'generating',
            primaryButtonDisabled: dialogStep.step === 'generating',
            onPrimaryButtonClick: handleGenerateLink,
            content: /*#__PURE__*/ _jsxs(Column, {
                gap: 8,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph1",
                        testId: ShareByLinkDialogTID.WarningIntro(),
                        children: shareByLinkTexts.warningIntro()
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph1",
                        testId: ShareByLinkDialogTID.PleaseNote(),
                        children: shareByLinkTexts.pleaseNote()
                    }),
                    /*#__PURE__*/ _jsxs(Column, {
                        gap: 4,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                testId: ShareByLinkDialogTID.BulletInvoiceIssued(),
                                children: "• ".concat(shareByLinkTexts.bulletInvoiceIssued())
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                testId: ShareByLinkDialogTID.BulletReminders(),
                                children: "• ".concat(shareByLinkTexts.bulletReminders())
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                testId: ShareByLinkDialogTID.BulletUniqueLink(),
                                children: "• ".concat(shareByLinkTexts.bulletUniqueLink())
                            })
                        ]
                    })
                ]
            })
        });
    }
    if (dialogStep.step === 'linkReady') {
        return /*#__PURE__*/ _jsx(ConfirmationModal, {
            bi: {
                primaryButton: {
                    name: 'share_by_link_close'
                },
                secondaryButton: {
                    name: 'share_by_link_close_secondary'
                }
            },
            testId: ShareByLinkDialogTID.Root(),
            title: shareByLinkTexts.title(),
            textAlignCenter: false,
            width: "520px",
            onCloseButtonClick: props.onClose,
            primaryButtonLabel: shareByLinkTexts.closeButton(),
            primaryButtonTestId: ShareByLinkDialogTID.CloseButton(),
            onPrimaryButtonClick: props.onClose,
            content: /*#__PURE__*/ _jsxs(Column, {
                gap: 12,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph1",
                        testId: ShareByLinkDialogTID.LinkReadyIntro(),
                        children: shareByLinkTexts.linkReadyIntro()
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        testId: ShareByLinkDialogTID.LinkReadyNote(),
                        children: shareByLinkTexts.linkReadyNote()
                    }),
                    /*#__PURE__*/ _jsxs(Row, {
                        gap: 8,
                        alignItems: "center",
                        justifyContent: "space-between",
                        children: [
                            /*#__PURE__*/ _jsxs(Column, {
                                gap: 2,
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraph1",
                                        weight: "bold",
                                        testId: ShareByLinkDialogTID.RecipientValue(),
                                        children: props.companyName
                                    }),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraph2",
                                        children: "".concat(props.contactName, " (").concat(props.email, ")")
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Badge, {
                                rounded: true,
                                outlined: true,
                                testId: ShareByLinkDialogTID.PrimaryContactBadge(),
                                children: shareByLinkTexts.primaryContact()
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(CopyToClipboard, {
                        value: linkUrl,
                        inlineButton: true,
                        buttonLabel: shareByLinkTexts.copyLinkButton(),
                        testId: ShareByLinkDialogTID.CopyLink()
                    })
                ]
            })
        });
    }
    const errorCloseHandler = dialogStep.submitSucceeded ? props.onClose : props.onCancel;
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        bi: {
            primaryButton: {
                name: 'share_by_link_error_close'
            },
            secondaryButton: {
                name: 'share_by_link_error_close_secondary'
            }
        },
        testId: ShareByLinkDialogTID.Root(),
        title: shareByLinkTexts.title(),
        textAlignCenter: false,
        width: "520px",
        onCloseButtonClick: errorCloseHandler,
        primaryButtonLabel: shareByLinkTexts.closeButton(),
        primaryButtonTestId: ShareByLinkDialogTID.CloseButton(),
        onPrimaryButtonClick: errorCloseHandler,
        content: /*#__PURE__*/ _jsx(Column, {
            gap: 8,
            children: /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph1",
                testId: ShareByLinkDialogTID.ErrorMessage(),
                children: dialogStep.submitSucceeded ? shareByLinkTexts.errorMessageLinkFailed() : shareByLinkTexts.errorMessageSubmitFailed()
            })
        })
    });
};
